import apiClient from "@/app/api/client";
import { endpoints } from "@/shared/api/endpoints";
import type { CancelledSalePaymentRecord, PaymentMethod } from "../types/payments.types";
import {
	type BackendPayment,
	type CreatePaymentFormValues,
	mapBackendPayment,
	toCreatePaymentRequest,
} from "./payments.mapper";

type SuccessEnvelope<T> = { success: boolean; data: T };

export type PaymentListParams = {
	dateFrom?: string;
	dateTo?: string;
	limit?: number;
	method?: PaymentMethod;
	page?: number;
	saleId?: string;
};

export type PaymentListResponse = {
	data: BackendPayment[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

export async function getPayments(params?: PaymentListParams) {
	const response = await apiClient.get<PaymentListResponse>(endpoints.payments.create, {
		params,
	});

	return {
		items: response.data.data.map(mapBackendPayment),
		meta: response.data.meta,
	};
}

export type UpdatePaymentFormValues = {
	amountUzs?: number;
	discountAmountUzs?: number;
	monthsPaid?: number;
	paymentMethod?: PaymentMethod;
	paymentDate?: string;
	receiptNumber?: string;
	note?: string;
};

export async function createPayment(values: CreatePaymentFormValues) {
	const response = await apiClient.post<SuccessEnvelope<BackendPayment>>(
		endpoints.payments.create,
		toCreatePaymentRequest(values)
	);

	return mapBackendPayment(response.data.data);
}

export async function getPayment(id: string) {
	const response = await apiClient.get<SuccessEnvelope<BackendPayment>>(
		endpoints.payments.detail(id)
	);

	return mapBackendPayment(response.data.data);
}

export async function updatePayment(id: string, values: UpdatePaymentFormValues) {
	const payload: Record<string, any> = {};
	if (values.amountUzs !== undefined) {
		payload.amount = Math.round(values.amountUzs);
	}
	if (values.discountAmountUzs !== undefined) {
		payload.discountAmount = Math.round(values.discountAmountUzs);
	}
	if (values.monthsPaid !== undefined) {
		payload.monthsPaid = values.monthsPaid;
	}
	if (values.paymentMethod !== undefined) {
		payload.paymentMethod = values.paymentMethod;
	}
	if (values.paymentDate !== undefined) {
		payload.paymentDate = values.paymentDate;
	}
	if (values.receiptNumber !== undefined) {
		payload.receiptNumber = values.receiptNumber.trim();
	}
	if (values.note !== undefined) {
		payload.note = values.note.trim();
	}

	const response = await apiClient.patch<SuccessEnvelope<BackendPayment>>(
		endpoints.payments.detail(id),
		payload
	);

	return mapBackendPayment(response.data.data);
}

export async function deletePayment(id: string) {
	await apiClient.delete(endpoints.payments.detail(id));
}

export type RefundPaymentInput = {
	amount: number;
	refundedAt: string;
	reason?: string;
};

export async function refundPayment(id: string, input: RefundPaymentInput) {
	const response = await apiClient.post<SuccessEnvelope<BackendPayment>>(
		endpoints.payments.refund(id),
		{
			amount: Math.round(input.amount),
			refundedAt: input.refundedAt,
			...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
		}
	);

	return mapBackendPayment(response.data.data);
}

type BackendCancelledSalePayment = {
	id: string;
	saleId: string;
	saleNumber: number | null;
	amount: number;
	paymentMethod: PaymentMethod;
	paymentDate: string;
	receiptNumber: string | null;
	note: string | null;
	refundedAmount: number;
	refundedAt: string | null;
	refundReason: string | null;
	cancelledAt: string | null;
	cancelReason: string | null;
	clientName: string;
	roomNumber: string;
	buildingName: string;
	receivedByName: string;
};

export type CancelledSalePaymentListParams = {
	page?: number;
	limit?: number;
};

export type CancelledSalePaymentListResponse = {
	data: BackendCancelledSalePayment[];
	meta: {
		limit: number;
		page: number;
		total: number;
		totalPages: number;
	};
};

function mapCancelledSalePayment(payment: BackendCancelledSalePayment): CancelledSalePaymentRecord {
	return {
		id: payment.id,
		saleId: payment.saleId,
		saleNumber: payment.saleNumber !== null ? String(payment.saleNumber) : "-",
		amountUzs: payment.amount,
		paymentMethod: payment.paymentMethod,
		paymentDate: payment.paymentDate,
		receiptNumber: payment.receiptNumber ?? "-",
		note: payment.note ?? undefined,
		refundedAmount: payment.refundedAmount,
		refundedAt: payment.refundedAt,
		refundReason: payment.refundReason,
		cancelledAt: payment.cancelledAt,
		cancelReason: payment.cancelReason,
		clientName: payment.clientName,
		roomNumber: payment.roomNumber,
		buildingName: payment.buildingName,
		receivedBy: payment.receivedByName,
	};
}

const CANCELLED_SALE_PAYMENTS_PAGE_SIZE = 100;

export async function getCancelledSalePayments(): Promise<CancelledSalePaymentRecord[]> {
	const items: CancelledSalePaymentRecord[] = [];
	let page = 1;

	while (true) {
		const response = await apiClient.get<CancelledSalePaymentListResponse>(
			endpoints.payments.cancelledSales,
			{ params: { page, limit: CANCELLED_SALE_PAYMENTS_PAGE_SIZE } }
		);
		items.push(...response.data.data.map(mapCancelledSalePayment));
		if (
			items.length >= (response.data.meta?.total ?? items.length) ||
			response.data.data.length === 0
		) {
			break;
		}
		page += 1;
	}

	return items;
}

export function getPaymentApiErrorMessage(error: unknown, fallback: string) {
	if (typeof error !== "object" || error === null || !("response" in error)) {
		return fallback;
	}

	const response = (error as { response?: { data?: { error?: { message?: unknown } } } }).response;
	return typeof response?.data?.error?.message === "string"
		? response.data.error.message
		: fallback;
}
